import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';

import { useLazyQuery, useSuspenseQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import { useFileDialog } from '@realadvisor/hooks';
import {
  FileText,
  Globe,
  Paperclip,
  Save,
  Signature,
  User,
  Users,
} from 'lucide-react';
import prettyBytes from 'pretty-bytes';
import {
  type DeepPartial,
  type ErrorOption,
  type FieldPath,
  type UseFormGetValues,
  type UseFormSetValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { v4 } from 'uuid';

import { gql } from '@graphql/__generated__';
import {
  type EmailTemplateFormFragment,
  type EmailTemplateFormLotSnippetDataFragment,
  type EmailTemplateSelectFragment,
  type EmailUserFragment,
  type SearchUsersQuery,
  type UserSelectFragment,
} from '@graphql/__generated__/graphql';
import {
  type RichEditorValue,
  createEmptyValue,
  createValueFromStored,
  updateSuggestions,
} from '@modules/_shared/components/email-editor/htmlValue';
import { toSuggestionLanguage } from '@modules/_shared/components/email-form/contextSuggestions';
import { EmailTemplatesDialog } from '@modules/_shared/components/EmailTemplatesDialog';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import {
  type FormDefinitionType,
  type FormFieldDefinitionType,
  type NonInternalProps,
  RaForm,
  type RaFormOnChange,
  createTagsElement,
  isFormSubmitDisabled,
} from '@modules/_shared/components/form/RaForm';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { SEARCH_USERS } from '@modules/_shared/components/user-select/UserSelect';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getEmailSenderSuggestions } from '@modules/_shared/utils/email';
import {
  type UploadFileFn,
  useFileUpload,
} from '@modules/_shared/utils/files/file-upload';
import { useWatchMultiple } from '@modules/_shared/utils/reactHookForm';
import { type Translate, useLocale } from '@shared/hooks/locale';

const createRichValue = (stored: string | null | undefined): RichEditorValue =>
  stored != null && stored !== ''
    ? createValueFromStored(stored)
    : createEmptyValue();

export const EMAIL_TEMPLATE_FORM_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateForm on email_templates {
    id
    name
    cc
    bcc
    subject
    text
    add_signature
    is_shared_with_organisation
    is_shared_with_everyone
    email_templates_files {
      email_template_id
      file_id
      file {
        id
        name
        url
        size
        mimetype
      }
    }
  }
`);

export const EMAIL_TEMPLATE_FORM_ENQUIRY_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateFormEnquiry on enquiries {
    id
    lot {
      ...EmailTemplateFormListing
    }
  }
`);

export const EMAIL_TEMPLATE_FORM_BUYER_LEAD_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateFormBuyerLead on buyer_leads {
    id
    lot {
      ...EmailTemplateFormListing
    }
  }
`);

export const EMAIL_TEMPLATE_FORM_LISTING_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateFormListing on lots {
    id
    tenant_id
    ...EmailListing
    lot_reference
    mandate_type
    brochure_doc_template_id
    broker {
      ...EmailUser
    }
    lead {
      id
      cma_reports(limit: 1, order_by: { updated_at: desc }) {
        id
        doc_template_id
      }
    }
    property {
      ...EmailProperty
      property_images(order_by: { order_nr: asc }, limit: 1) {
        id
        image {
          id
          url
        }
      }
    }
  }
`);

export const EMAIL_TEMPLATE_FORM_LEAD_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateFormLead on leads {
    id
    cma_reports(limit: 1, order_by: { updated_at: desc }) {
      id
      doc_template_id
    }
    lead_agents(where: { passed_at: { _is_null: true } }) {
      id
      user {
        ...EmailUser
      }
    }
    lots {
      id
      ...EmailListing
      lot_reference
      mandate_type
    }
    property {
      ...EmailProperty
      property_images(order_by: { order_nr: asc }, limit: 1) {
        id
        image {
          id
          url
        }
      }
    }
  }
`);

export const EMAIL_TEMPLATE_FORM_LOT_SNIPPET_DATA_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateFormLotSnippetData on lots {
    id
    tenant_id
    title
    lot_reference
    mandate_type
    rent_net
    rent_extra
    sale_price
    brochure_doc_template_id
    property {
      id
      living_surface
      number_of_rooms
      street_number
      route
      locality
      postcode
      property_type {
        id
        label
      }
      property_images(order_by: { order_nr: asc }, limit: 1) {
        id
        image {
          id
          url
        }
      }
    }
  }
`);

const GET_EMAIL_TEMPLATE_FORM_DATA = gql(/* GraphQL */ `
  query getEmailTemplateFormData(
    $ids: [uuid!]!
    $skipUsers: Boolean!
    $skipSuggestions: Boolean!
    $language: GetEmailTemplateSuggestionsLanguage
    $buyerLeadId: uuid
    $leadId: uuid
    $listingId: uuid
    $enquiryId: uuid
    $developmentId: uuid
    $senderId: uuid
    $recipients: [uuid!]
  ) {
    users(where: { id: { _in: $ids } }) @skip(if: $skipUsers) {
      id
      ...UserSelect
    }

    get_email_template_suggestions(
      senderId: $senderId
      language: $language
      recipients: $recipients
      listingId: $listingId
      enquiryId: $enquiryId
      buyerLeadId: $buyerLeadId
      leadId: $leadId
      developmentId: $developmentId
    ) @skip(if: $skipSuggestions) {
      suggestions
    }
  }
`);

const GET_SENDER_SUGGESTIONS_DATA = gql(/* GraphQL */ `
  query getSenderSuggestionsData($id: uuid!) {
    users_by_pk(id: $id) {
      ...EmailUser
    }
  }
`);

// Raw template variable keys, offered by the `#` autocomplete when there is no
// suggestion context (standalone template editing). They are stored verbatim
// and resolved with real values when the template is applied in an email
// context (get_email_template_suggestions).
const USER_FIELDS = [
  'firstName',
  'lastName',
  'title',
  'primaryPhoneNumber',
  'primaryEmail',
  'organisationName',
  'organisationAddress',
  'teamName',
  'teamAddress',
];

const AGENT_FIELDS = [
  '#agent_firstName',
  '#agent_lastName',
  '#agent_title',
  '#agent_primaryPhoneNumber',
  '#agent_primaryEmail',
  '#agent_organisationName',
  '#agent_organisationAddress',
];

const PROPERTY_FIELDS = [
  '#property_municipality',
  '#property_route',
  '#property_postcode',
  '#property_streetNumber',
  '#property_state',
  '#property_fullAddress',
  '#property_type',
  '#property_numberOfRooms',
  '#property_livingSurface',
  '#property_landSurface',
  '#property_latestAppraisal_realadvisor_max',
  '#property_latestAppraisal_realadvisor_value',
  '#property_latestAppraisal_realadvisor_min',
  '#property_latestAppraisal_reportURL',
  '#property_lot_brochureURL',
  '#property_latestCMA_cmaURL',
];

const LOT_FIELDS = [
  '#property_lot_title',
  '#property_lot_salePrice',
  '#property_lot_rentNetPrice',
  '#property_lot_rentExpenses',
  '#property_lot_rentGrossPrice',
];

export const STATIC_TEMPLATE_SUGGESTION_KEYS = [
  ...USER_FIELDS.map(f => `#recipient.${f}`),
  ...USER_FIELDS.map(f => `#sender.${f}`),
  ...PROPERTY_FIELDS,
  ...AGENT_FIELDS,
  ...LOT_FIELDS,
];

const STATIC_TEMPLATE_SUGGESTIONS: Record<string, string | null> =
  Object.fromEntries(STATIC_TEMPLATE_SUGGESTION_KEYS.map(k => [k, null]));

type FileUploadData = {
  id: string;
  mimetype?: string | null;
  name: string;
  size: number;
  url: string;
};
type EmailMessageRecipientInput = {
  id?: string;
  email: string;
  name?: string | null;
  user_id?: string;
};
type EmailTemplateFormData = Pick<
  EmailTemplateFormFragment,
  | 'name'
  | 'add_signature'
  | 'is_shared_with_organisation'
  | 'is_shared_with_everyone'
> & {
  id?: string;
  cc: (EmailMessageRecipientInput | UserSelectFragment)[];
  bcc: (EmailMessageRecipientInput | UserSelectFragment)[];
  from?: string | null;
  files: FileUploadData[];
  subject: RichEditorValue;
  text: RichEditorValue;
};
type FormInputField = Extract<
  keyof EmailTemplateFormData,
  'name' | 'cc' | 'bcc' | 'from'
>;

export type EmailTemplateInput = Pick<
  EmailTemplateFormData,
  'id' | 'name' | 'files' | 'from'
> & {
  cc?: EmailMessageRecipientInput[];
  bcc?: EmailMessageRecipientInput[];
  subject: string;
  text: string;
  textHTML: string;
  textPlaintext: string;
  subjectPlaintext: string;
  addSignature: boolean;
  isSharedWithOrganisation: boolean;
  isSharedWithEveryone: boolean;
};

type SubmitFormHandler = (data: EmailTemplateInput) => Promise<any>;

type EmailTemplateFormProps = {
  isDialog?: boolean;
  onClose?: () => void;
  onSubmit: SubmitFormHandler;
  emailTemplate:
    | EmailTemplateFormFragment
    | { text: RichEditorValue; subject: RichEditorValue }
    | undefined;
  initialSender?: EmailUserFragment | null;
  isTemplateShareable: boolean;
  fields?: FormInputField[];
  disabled?: boolean;
  allowSubmitNotDirty?: boolean;
  onChange?: RaFormOnChange<EmailTemplateFormData>;
  lotSnippetData?: EmailTemplateFormLotSnippetDataFragment;
  enquiryId?: string;
  leadId?: string;
  buyerLeadId?: string;
  developmentId?: string;
};

type EmailExtraData = Pick<
  EmailTemplateFormProps,
  'enquiryId' | 'buyerLeadId' | 'lotSnippetData' | 'leadId' | 'developmentId'
>;

type EmailTemplateFormFieldsProps = Pick<
  EmailTemplateFormProps,
  | 'fields'
  | 'onSubmit'
  | 'emailTemplate'
  | 'isTemplateShareable'
  | 'disabled'
  | 'allowSubmitNotDirty'
  | 'initialSender'
  | 'onChange'
> & {
  onCancel?: () => void;
  extraData: EmailExtraData;
};

const FileAttachments: React.FC<{
  isLoading: boolean;
}> = ({ isLoading }) => {
  const files = useWatch<EmailTemplateFormData, 'files'>({ name: 'files' });
  const { setValue, getValues } = useFormContext<EmailTemplateFormData>();

  const onFileRemoved = (file: FileUploadData) => {
    const prevFiles = getValues('files');
    setValue(
      'files',
      prevFiles.filter(f => f.id !== file.id),
      {
        shouldDirty: true,
      },
    );
  };

  if (!isLoading && files.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={1}
      direction="row"
      sx={{
        mt: 2,
        flexWrap: 'wrap',
        justifyContent: files.length > 0 ? 'flex-start' : 'center',
        mb: -1,
      }}
    >
      {files.map(file => (
        <Chip
          key={`${file.name}-${file.id}`}
          label={`${file.name} (${prettyBytes(file.size ?? 0)})`}
          icon={<FileIcon mimetype={file.mimetype ?? null} />}
          onDelete={() => onFileRemoved(file)}
          sx={{ mb: `8px !important` }}
        />
      ))}
      {isLoading && (
        <CircularProgress
          size={24}
          disableShrink={true}
          sx={{ alignSelf: 'center' }}
        />
      )}
    </Stack>
  );
};

const isUserFragment = (
  value: EmailMessageRecipientInput | UserSelectFragment,
): value is UserSelectFragment => 'primaryEmail' in value;

const createAddressFieldElement = <
  TPath extends FieldPath<EmailTemplateFormData>,
>(
  name: TPath,
  label: string,
  filterUser: (value: string) => Promise<UserSelectFragment[]>,
  t: Translate,
  disabled: boolean,
) =>
  createTagsElement<
    EmailTemplateFormData,
    EmailMessageRecipientInput | UserSelectFragment
  >(name, label, filterUser, {
    gridProps: { md: 12 },
    renderOption: (props, option) => {
      const user = isUserFragment(option)
        ? option
        : { first_name: option.email, full_name: option.name };
      const secondary = isUserFragment(option)
        ? option.primaryEmail[0]?.email
        : undefined;

      return (
        <ListItem {...props} key={option.id}>
          <ListItemAvatar>
            <UserAvatar user={user} size={40} />
          </ListItemAvatar>
          <ListItemText primary={user.full_name} secondary={secondary} />
        </ListItem>
      );
    },
    getOptionLabel: option =>
      (isUserFragment(option) ? option.full_name : option.name) ?? '',
    isOptionEqualToValue: (option, value) => option.id === value.id,
    renderTags: (
      value: (EmailMessageRecipientInput | UserSelectFragment)[],
      getTagProps: (params: { index: number }) => {
        key: number | string;
        [k: string]: unknown;
      },
    ) => {
      return value.map(
        (
          option: EmailMessageRecipientInput | UserSelectFragment,
          index: number,
        ) => {
          const { key, ...tagProps } = getTagProps({ index });
          const tooltip = isUserFragment(option)
            ? option.primaryEmail[0]?.email
            : option.email;

          return (
            <Tooltip title={tooltip} key={key}>
              <Chip
                sx={{ '& .MuiAvatar-root': { ml: 0.5, mr: -0.5 } }}
                size="small"
                label={
                  (isUserFragment(option)
                    ? option.full_name
                    : option.name ?? option.email) ?? ''
                }
                avatar={
                  <UserAvatar
                    user={
                      isUserFragment(option)
                        ? option
                        : { first_name: option.name ?? option.email }
                    }
                    size={18}
                  />
                }
                {...tagProps}
              />
            </Tooltip>
          );
        },
      );
    },
    InputProps: {
      startAdornment: <User size={20} color="var(--mui-palette-grey-700)" />,
      placeholder: t('Search users'),
    },
    disabled: () => disabled,
  });

const extractUserIds = (
  cc?: EmailMessageRecipientInput[],
  bcc?: EmailMessageRecipientInput[],
) =>
  Array.from(
    new Set(
      [...(cc ?? []), ...(bcc ?? [])]
        .map(u => u.user_id)
        .filter(v => v != null),
    ),
  );

const hasInvalidRecipients = (
  recipients: (EmailMessageRecipientInput | UserSelectFragment)[],
) =>
  recipients.some(user => {
    const email = isUserFragment(user)
      ? user.primaryEmail[0]?.email
      : user.email;

    return email == null || email === '';
  });

const toRecipients = (
  users: (EmailMessageRecipientInput | UserSelectFragment)[],
) => {
  return users
    .map(user =>
      isUserFragment(user)
        ? {
            email: user.primaryEmail[0]?.email,
            name: [user.first_name, user.last_name]
              .filter(v => v != null)
              .join(' '),
            user_id: user.id,
          }
        : user,
    )
    .filter(v => v != null);
};

export const EmailTemplateForm: React.FC<EmailTemplateFormProps> = props => {
  const {
    isDialog,
    onSubmit,
    onClose,
    isTemplateShareable,
    fields,
    emailTemplate,
    disabled,
    allowSubmitNotDirty,
    initialSender,
    onChange,
    leadId,
    lotSnippetData,
    buyerLeadId,
    enquiryId,
    developmentId,
  } = props;

  const { t } = useLocale();

  return isDialog === true ? (
    <SlideDialog
      open={true}
      onClose={onClose ?? (() => {})}
      title={emailTemplate != null ? t('Template') : t('Add template')}
      maxWidth="md"
      footerActions={<></>}
      sx={{ '& .MuiDialog-paper': { outline: 'none' } }}
    >
      {disabled && (
        <div
          css={{
            position: 'absolute',
            borderRadius: 4,
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />
      )}
      <Box sx={{ p: 2 }}>
        <Suspense fallback={null}>
          <EmailTemplateFormFields
            emailTemplate={emailTemplate}
            initialSender={initialSender}
            isTemplateShareable={isTemplateShareable}
            allowSubmitNotDirty={allowSubmitNotDirty}
            onSubmit={onSubmit}
            disabled={disabled}
            fields={fields}
            onCancel={onClose}
            onChange={onChange}
            extraData={{
              enquiryId,
              lotSnippetData,
              leadId,
              buyerLeadId,
              developmentId,
            }}
          />
        </Suspense>
      </Box>
    </SlideDialog>
  ) : (
    <Suspense fallback={null}>
      <EmailTemplateFormFields
        emailTemplate={emailTemplate}
        initialSender={initialSender}
        isTemplateShareable={isTemplateShareable}
        allowSubmitNotDirty={allowSubmitNotDirty}
        onSubmit={onSubmit}
        disabled={disabled}
        fields={fields}
        onChange={onChange}
        extraData={{
          enquiryId,
          lotSnippetData,
          leadId,
          buyerLeadId,
          developmentId,
        }}
      />
    </Suspense>
  );
};

const RaEmailTemplateDialog: React.FC<{
  onClose: (template?: EmailTemplateSelectFragment) => void;
  onRichValueChanged: (value: RichEditorValue) => RichEditorValue;
  open: boolean;
}> = ({ onClose, open, onRichValueChanged }) => {
  const { setValue } = useFormContext<EmailTemplateFormData>();

  return (
    <EmailTemplatesDialog
      onClose={onClose}
      onTemplateSelect={template => {
        setValue('name', template.name ?? '', {
          shouldDirty: true,
        });
        setValue('add_signature', template.add_signature ?? true, {
          shouldDirty: true,
        });
        setValue(
          'files',
          template.email_templates_files.flatMap(({ file }) =>
            file != null ? [file] : [],
          ),
          {
            shouldDirty: true,
          },
        );
        setValue(
          'subject',
          onRichValueChanged(createRichValue(template.subject)),
          {
            shouldDirty: true,
          },
        );
        setValue('text', onRichValueChanged(createRichValue(template.text)), {
          shouldDirty: true,
        });

        onClose(template);
      }}
      open={open}
    />
  );
};

const AttachFilesBtn: React.FC<{
  disabled?: boolean;
  setValue: UseFormSetValue<EmailTemplateFormData>;
  getValues: UseFormGetValues<EmailTemplateFormData>;
  uploadFile: UploadFileFn;
}> = ({ disabled, setValue, uploadFile, getValues }) => {
  const { t } = useLocale();
  const [erroredField, setErroredField] = useState<
    { id: string; file: string; error: any }[]
  >([]);

  const handleUpload = async (files: File[]) => {
    const filesWithId = files.map(file => ({ id: v4(), file }));

    const uploadResults = await Promise.allSettled(
      filesWithId.map(({ id, file }) => {
        return new Promise<FileUploadData>((resolve, reject) => {
          uploadFile(file, ({ data, error }) => {
            if (data) {
              return resolve({
                id,
                name: file.name,
                url: data.url,
                size: file.size,
                mimetype: file.type,
              });
            }

            reject({ id, file: file.name, error });
          });
        });
      }),
    );

    const uploadedFiles = uploadResults
      .filter(
        (result): result is PromiseFulfilledResult<FileUploadData> =>
          result.status === 'fulfilled',
      )
      .map(result => result.value);

    setErroredField(
      uploadResults
        .filter(
          (result): result is PromiseRejectedResult =>
            result.status === 'rejected',
        )
        .map(result => result.reason),
    );

    if (uploadedFiles.length > 0) {
      const prevFiles = getValues('files');
      setValue('files', [...prevFiles, ...uploadedFiles], {
        shouldDirty: true,
      });
    }
  };

  const openFileDialog = useFileDialog({
    multiple: true,
    onChange: files => {
      if (files.length === 0) {
        return;
      }

      handleUpload(files);
    },
  });

  return (
    <>
      <Tooltip title={t('Upload a file')} placement="top">
        <IconButton onClick={openFileDialog} disabled={disabled}>
          <Paperclip size={20} />
        </IconButton>
      </Tooltip>
      <Snackbar
        open={erroredField.length > 0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={5000}
        onClose={() => setErroredField([])}
      >
        <Alert
          severity="error"
          variant="filled"
          sx={{ width: '100%', alignItems: 'center' }}
        >
          <AlertTitle>
            {t('The following files could not be attached:')}
          </AlertTitle>
          <ul>
            {erroredField.map(({ file, id }) => (
              <li key={id} css={{ mb: 8 }}>
                {file}
              </li>
            ))}
          </ul>
        </Alert>
      </Snackbar>
    </>
  );
};

const FormActionButtons: React.FC<{
  defaultValues: DeepPartial<EmailTemplateFormData>;
  disabled?: boolean;
  isTemplateShareable: boolean;
  uploadFile: UploadFileFn;
  onTemplateShowClick: () => void;
  onCancel?: () => void;
  allowSubmitNotDirty?: boolean;
}> = ({
  defaultValues,
  isTemplateShareable,
  onTemplateShowClick,
  disabled = false,
  uploadFile,
  onCancel,
  allowSubmitNotDirty,
}) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const {
    reset,
    formState: { isSubmitting, isDirty },
    setValue,
    getValues,
  } = useFormContext<EmailTemplateFormData>();
  const {
    add_signature,
    is_shared_with_everyone,
    is_shared_with_organisation,
  } = useWatchMultiple<EmailTemplateFormData>({
    name: [
      'add_signature',
      'is_shared_with_organisation',
      'is_shared_with_everyone',
    ],
  });
  const [openTooltip, setTooltipOpen] = useState(false);

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTooltipOpen = () => {
    if (disabled !== true) {
      setTooltipOpen(true);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        mt: 3,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'flex-start',
          flex: 1,
        }}
      >
        <AttachFilesBtn
          disabled={disabled}
          setValue={setValue}
          getValues={getValues}
          uploadFile={uploadFile}
        />

        <Tooltip
          title={t('chooseTemplate')}
          placement="top"
          open={openTooltip}
          onClose={handleTooltipClose}
          onOpen={handleTooltipOpen}
        >
          <IconButton onClick={onTemplateShowClick} disabled={disabled}>
            <FileText size={20} />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('addSignature')} placement="top">
          <IconButton
            onClick={() =>
              setValue('add_signature', !add_signature, { shouldDirty: true })
            }
            disabled={disabled}
            color={add_signature ? 'primary' : 'default'}
          >
            <Signature size={20} />
          </IconButton>
        </Tooltip>
        {isTemplateShareable === true && (
          <>
            <Tooltip title={t('Share with my teams')} placement="top">
              <span>
                <IconButton
                  onClick={() =>
                    setValue(
                      'is_shared_with_organisation',
                      !is_shared_with_organisation,
                      { shouldDirty: true },
                    )
                  }
                  disabled={disabled || is_shared_with_everyone === true}
                  color={is_shared_with_organisation ? 'primary' : 'default'}
                >
                  <Users size={20} />
                </IconButton>
              </span>
            </Tooltip>
            {isAdmin && (
              <Tooltip title={t('Share with everyone')} placement="top">
                <IconButton
                  onClick={() => {
                    const next = !is_shared_with_everyone;
                    setValue('is_shared_with_everyone', next, {
                      shouldDirty: true,
                    });
                    setValue('is_shared_with_organisation', next, {
                      shouldDirty: true,
                    });
                  }}
                  disabled={disabled}
                  color={is_shared_with_everyone ? 'primary' : 'default'}
                >
                  <Globe size={20} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="outlined"
          color="neutral"
          disabled={isSubmitting}
          onClick={() => {
            reset(defaultValues);
            onCancel?.();
          }}
        >
          {t('Cancel')}
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loadingIndicator={
            <CircularProgress disableShrink color="inherit" size={16} />
          }
          disabled={isFormSubmitDisabled(
            disabled,
            isDirty,
            allowSubmitNotDirty,
          )}
          loading={isSubmitting}
          loadingPosition="start"
          startIcon={<Save size={20} />}
        >
          {t('Save')}
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

const EmailTemplateFormFields: React.FC<
  EmailTemplateFormFieldsProps
> = props => {
  const {
    onSubmit,
    onCancel,
    isTemplateShareable,
    fields,
    emailTemplate,
    initialSender,
    disabled = false,
    allowSubmitNotDirty,
    onChange,
    extraData: {
      enquiryId,
      lotSnippetData,
      leadId,
      buyerLeadId,
      developmentId,
    },
  } = props;

  const { t, language } = useLocale();
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [richEditorResetKey, resetRichEditor] = useReducer(k => k + 1, 0);
  const [upload, uploadingFile] = useFileUpload('file');
  const [uploadImage] = useFileUpload('image');
  const handleImageUpload = useCallback(
    (file: File) =>
      new Promise<string | null>((resolve, reject) => {
        uploadImage(file, ({ data, error: uploadError }) => {
          if (uploadError) {
            reject(uploadError);
            return;
          }
          resolve(data?.url ?? null);
        });
      }),
    [uploadImage],
  );
  const [sender, setSender] = useState(initialSender);
  const users =
    emailTemplate != null && 'id' in emailTemplate
      ? extractUserIds(emailTemplate?.cc, emailTemplate?.bcc)
      : [];
  useEffect(() => {
    setSender(initialSender);
  }, [initialSender]);

  // Standalone usage (no listing/lead/enquiry/development context) has no
  // basis for resolved suggestions — skip the action (it would 400) and fall
  // back to the raw static keys.
  const hasSuggestionContext =
    lotSnippetData?.id != null ||
    enquiryId != null ||
    buyerLeadId != null ||
    leadId != null ||
    developmentId != null;

  const [searchUsers] = useLazyQuery<SearchUsersQuery>(SEARCH_USERS);
  const { data: defaultData } = useSuspenseQuery(GET_EMAIL_TEMPLATE_FORM_DATA, {
    variables: {
      ids: users,
      skipUsers: users.length === 0,
      skipSuggestions: !hasSuggestionContext,
      senderId: initialSender?.id,
      buyerLeadId,
      leadId,
      listingId: lotSnippetData?.id,
      enquiryId,
      developmentId,
      language: toSuggestionLanguage(language),
    },
  });
  const [getSenderSuggestionsData] = useLazyQuery(GET_SENDER_SUGGESTIONS_DATA);

  const defaultUsers = defaultData?.users;
  const suggestions = hasSuggestionContext
    ? (defaultData?.get_email_template_suggestions?.suggestions as
        | Record<string, string | null>
        | undefined
        | null)
    : STATIC_TEMPLATE_SUGGESTIONS;

  const filterUser = useCallback(
    async (value: string) => {
      const response = await searchUsers({
        variables: {
          q: value,
          where: { emails: { email: { _is_null: false } } },
        },
      });

      return response.data?.search_users_tsv ?? [];
    },
    [searchUsers],
  );

  const getTemplateSuggestions = useCallback(
    (senderData: EmailUserFragment | null) => {
      if (suggestions == null) {
        return [];
      }

      const senderSuggestions = getEmailSenderSuggestions(senderData, t);

      return Object.entries(suggestions).map(([key, value]) => ({
        id: key,
        name: key,
        value:
          senderSuggestions.find(suggestion => suggestion.id === key)?.name ??
          value ??
          key,
      }));
    },
    [t, suggestions],
  );

  const refreshEditorValueWithSuggestions = useCallback(
    (richValue: RichEditorValue, senderData: EmailUserFragment | null) => {
      const templateSuggestions = getTemplateSuggestions(senderData);

      return updateSuggestions(
        richValue,
        Object.fromEntries(
          templateSuggestions.map(({ id, name, value }) => [
            id,
            { name, value },
          ]),
        ),
      );
    },
    [getTemplateSuggestions],
  );

  const handleChange = useCallback<RaFormOnChange<EmailTemplateFormData>>(
    (data, name, type, helpers) => {
      const refreshEditorFields = (senderData: EmailUserFragment | null) => {
        if (data.text != null) {
          helpers.setValue(
            'text',
            refreshEditorValueWithSuggestions(
              data.text as RichEditorValue,
              senderData,
            ),
            { shouldDirty: true },
          );
        }
        if (data.subject != null) {
          helpers.setValue(
            'subject',
            refreshEditorValueWithSuggestions(
              data.subject as RichEditorValue,
              senderData,
            ),
            { shouldDirty: true },
          );
        }
        if (data.text != null || data.subject != null) {
          resetRichEditor();
        }
      };

      if (type === 'change' && name === 'from') {
        if (data.from != null) {
          getSenderSuggestionsData({ variables: { id: data.from } }).then(
            ({ data: senderData }) => {
              const resolvedSender = senderData?.users_by_pk ?? null;
              setSender(resolvedSender);
              refreshEditorFields(resolvedSender);
            },
          );
        } else {
          setSender(null);
        }
      } else if (type == null && name == null) {
        setSender(initialSender);
        refreshEditorFields(initialSender ?? null);
      }

      onChange?.(data, name, type, helpers);
    },
    [
      getSenderSuggestionsData,
      onChange,
      initialSender,
      refreshEditorValueWithSuggestions,
    ],
  );

  const formDefinition = useMemo<FormDefinitionType<EmailTemplateFormData>>(
    () => () => {
      const definition: FormFieldDefinitionType<EmailTemplateFormData>[] = [];
      const suggestions = getTemplateSuggestions(sender ?? null);

      if (fields?.includes('name')) {
        definition.push({
          name: 'name',
          label: t('Template name'),
          type: 'text',
          disabled: () => disabled,
        });
      }

      if (fields?.includes('from')) {
        definition.push({
          name: 'from',
          label: t('From'),
          type: 'user',
          gridProps: { md: 12 },
          filters: { is_broker: { _eq: true } },
          disabled: () => disabled,
        });
      }

      if (fields?.includes('cc')) {
        definition.push(
          createAddressFieldElement('cc', t('Cc'), filterUser, t, disabled),
        );
      }

      if (fields?.includes('bcc')) {
        definition.push(
          createAddressFieldElement('bcc', t('Bcc'), filterUser, t, disabled),
        );
      }

      definition.push(
        {
          name: 'subject',
          type: 'rich-editor-suggestions',
          label: t('Subject'),
          singleLine: true,
          suggestions,
          gridProps: { md: 12 },
          resetKey: `subject-${richEditorResetKey}`,
          disabled: () => disabled,
        },
        {
          name: 'text',
          type: 'rich-editor-suggestions',
          label: t('Content'),
          suggestions,
          gridProps: { md: 12 },
          resetKey: `text-${richEditorResetKey}`,
          disabled: () => disabled,
          snippetLotId: lotSnippetData?.id,
          snippetDevelopmentId: developmentId,
          onImageUpload: handleImageUpload,
        },
      );

      return definition;
    },
    [
      fields,
      t,
      filterUser,
      richEditorResetKey,
      getTemplateSuggestions,
      sender,
      developmentId,
      lotSnippetData,
      disabled,
      handleImageUpload,
    ],
  );

  const toDbUser = useCallback(
    (user: EmailMessageRecipientInput) => {
      const dbUser = defaultUsers?.find(
        u => user.user_id != null && u.id === user.user_id,
      );

      return dbUser != null
        ? {
            ...dbUser,
            full_name: user.name ?? dbUser.full_name,
            primaryEmail: [{ email: user.email }],
          }
        : { ...user, id: user.user_id };
    },
    [defaultUsers],
  );

  const initialValues = useMemo(() => {
    const values: DeepPartial<EmailTemplateFormData> = {
      name: '',
      from: initialSender?.id,
      cc: [],
      bcc: [],
      add_signature: true,
      is_shared_with_organisation: false,
      is_shared_with_everyone: false,
      files: [],
    };

    if (emailTemplate == null) {
      values.subject = createEmptyValue();
      values.text = createEmptyValue();
    } else if ('id' in emailTemplate) {
      const cc: EmailMessageRecipientInput[] =
        emailTemplate.cc != null && Array.isArray(emailTemplate.cc)
          ? emailTemplate.cc
          : [];
      const bcc: EmailMessageRecipientInput[] =
        emailTemplate.bcc != null && Array.isArray(emailTemplate.bcc)
          ? emailTemplate.bcc
          : [];

      values.name = emailTemplate.name ?? '';
      values.cc = cc.map(toDbUser);
      values.bcc = bcc.map(toDbUser);
      values.add_signature = emailTemplate.add_signature;
      values.is_shared_with_organisation =
        emailTemplate.is_shared_with_organisation;
      values.is_shared_with_everyone = emailTemplate.is_shared_with_everyone;
      values.files = emailTemplate.email_templates_files.flatMap(({ file }) =>
        file != null ? [file] : [],
      );
      values.subject = createRichValue(emailTemplate.subject);
      values.text = createRichValue(emailTemplate.text);
    } else {
      values.subject = emailTemplate.subject;
      values.text = emailTemplate.text;
    }

    return values;
  }, [emailTemplate, toDbUser, initialSender]);

  const handleSubmit = useCallback(
    (
      data: NonInternalProps<EmailTemplateFormData>,
      unStrippedData: EmailTemplateFormData,
    ) =>
      onSubmit({
        id:
          emailTemplate != null && 'id' in emailTemplate
            ? emailTemplate.id
            : undefined,
        name: data.name,
        cc: toRecipients(data.cc),
        bcc: toRecipients(data.bcc),
        from: data.from,
        subject: unStrippedData.subject.getHtml(),
        text: unStrippedData.text.getHtml(),
        textHTML: unStrippedData.text.toString('html', t, language),
        textPlaintext: unStrippedData.text.toString('plaintext'),
        subjectPlaintext: unStrippedData.subject.toString('plaintext'),
        addSignature: data.add_signature,
        isSharedWithOrganisation: data.is_shared_with_organisation,
        isSharedWithEveryone: data.is_shared_with_everyone,
        files: data.files,
      }),
    [onSubmit, emailTemplate, t, language],
  );

  const validateEmailFormData = useCallback(
    (values: EmailTemplateFormData) => {
      const errors: [FieldPath<EmailTemplateFormData>, ErrorOption][] = [];

      const messageIsEmpty = values.text.isEmpty();

      const subjectIsEmpty = values.subject.isEmpty();

      if (fields?.includes('name') && values.name === '') {
        errors.push([
          'name',
          { type: 'required', message: t('A template name is required') },
        ]);
      }

      if (messageIsEmpty && subjectIsEmpty) {
        errors.push(
          [
            'text',
            {
              type: 'required',
              message: t('A message or a subject is required'),
            },
          ],
          [
            'subject',
            {
              type: 'required',
              message: t('A message or a subject is required'),
            },
          ],
        );
      }

      if (hasInvalidRecipients(values.cc)) {
        errors.push([
          'cc',
          {
            type: 'validate',
            message: t('One or several recipients have no primary email'),
          },
        ]);
      }

      if (hasInvalidRecipients(values.bcc)) {
        errors.push([
          'bcc',
          {
            type: 'validate',
            message: t('One or several recipients have no primary email'),
          },
        ]);
      }

      return errors;
    },
    [t, fields],
  );

  return (
    <RaForm
      formDefinition={formDefinition}
      defaultValues={initialValues}
      gridProps={{ sx: { padding: 0 } }}
      onChange={handleChange}
      actionButtonsComponent={
        <FormActionButtons
          disabled={disabled}
          defaultValues={initialValues}
          onTemplateShowClick={() => setTemplatesOpen(true)}
          isTemplateShareable={isTemplateShareable}
          uploadFile={upload}
          allowSubmitNotDirty={allowSubmitNotDirty}
          onCancel={() => {
            resetRichEditor();
            onCancel?.();
          }}
        />
      }
      onSubmit={handleSubmit}
      validate={validateEmailFormData}
    >
      <RaEmailTemplateDialog
        onRichValueChanged={newValue =>
          refreshEditorValueWithSuggestions(newValue, sender ?? null)
        }
        onClose={template => {
          setTemplatesOpen(false);

          if (template != null) {
            resetRichEditor();
          }
        }}
        open={templatesOpen}
      />
      <FileAttachments isLoading={uploadingFile} />
    </RaForm>
  );
};
